import React, { useState, useEffect } from "react";
import styles from "./AffiliateScreen.module.css";
import { vietnamBanks } from "../constants/vietnamBanks"; // I will create this file

const AffiliatePayoutManager = ({ userId }) => {
  const [bankAccount, setBankAccount] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [bankForm, setBankForm] = useState({
    bankName: "",
    accountNumber: "",
    accountHolder: "",
  });
  const [stats, setStats] = useState({ availableBalance: 0 });

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

  const fetchPayoutData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("userAccessToken")}` };
      
      const [bankRes, withdrawRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/users/${userId}/bank-account`, { headers }),
        fetch(`${API_URL}/api/users/${userId}/withdrawals`, { headers }),
        fetch(`${API_URL}/api/affiliate/stats?userId=${userId}`, { headers })
      ]);

      if (bankRes.ok) {
        const data = await bankRes.json();
        if (data.bankAccount) {
          setBankAccount(data.bankAccount);
          setBankForm(data.bankAccount);
        }
      }

      if (withdrawRes.ok) {
        const data = await withdrawRes.json();
        if (data.withdrawals) {
          setWithdrawals(data.withdrawals);
        }
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        if (data.stats) {
          setStats(data.stats); // Assuming it has totalEarnings and pendingCommission
        }
      }
    } catch (error) {
      console.error("Lỗi khi tải dữ liệu thanh toán:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchPayoutData();
    }
  }, [userId]);

  const handleSaveBank = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
      };
      
      const res = await fetch(`${API_URL}/api/users/${userId}/bank-account`, {
        method: "PUT",
        headers,
        body: JSON.stringify(bankForm),
      });
      const data = await res.json();
      if (data.success) {
        alert("Đã lưu thông tin tài khoản ngân hàng!");
        setBankAccount(bankForm);
      } else {
        alert(data.message || "Lỗi khi lưu thông tin");
      }
    } catch (error) {
      alert("Lỗi kết nối");
    } finally {
      setSaving(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!bankAccount) {
      alert("Vui lòng thiết lập tài khoản ngân hàng trước khi rút tiền.");
      return;
    }
    const amount = Number(withdrawAmount);
    if (isNaN(amount) || amount < 200000) {
      alert("Số tiền rút tối thiểu là 200.000đ");
      return;
    }
    // Assume available is totalEarnings - sum(withdrawals approved/pending/completed)
    // Actually we can let backend validate
    setWithdrawing(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
      };
      
      const res = await fetch(`${API_URL}/api/users/${userId}/withdraw`, {
        method: "POST",
        headers,
        body: JSON.stringify({ amount }),
      });
      const data = await res.json();
      if (data.success) {
        alert("Đã gửi yêu cầu rút tiền thành công!");
        setWithdrawAmount("");
        fetchPayoutData(); // Reload
      } else {
        alert(data.message || "Lỗi khi rút tiền");
      }
    } catch (error) {
      alert("Lỗi kết nối");
    } finally {
      setWithdrawing(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'approved': return 'text-blue-600 bg-blue-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };
  
  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return 'Chờ duyệt';
      case 'approved': return 'Đã duyệt';
      case 'completed': return 'Hoàn tất';
      case 'rejected': return 'Từ chối';
      default: return status;
    }
  };

  if (loading) return <div className="p-4 text-center">Đang tải...</div>;

  return (
    <div className="space-y-8 mt-6">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-xl font-bold mb-4 text-gray-800">Thiết lập tài khoản nhận tiền</h3>
        <p className="text-gray-600 mb-6 text-sm">Vui lòng điền thông tin tài khoản ngân hàng chính xác để chúng tôi chuyển hoa hồng cho bạn.</p>
        
        <form onSubmit={handleSaveBank} className="space-y-4 max-w-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ngân hàng</label>
            <select 
              required
              value={bankForm.bankName}
              onChange={(e) => setBankForm({...bankForm, bankName: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Chọn ngân hàng</option>
              {vietnamBanks.map((bank) => (
                <option key={bank.bin || bank.shortName} value={bank.shortName}>
                  {bank.shortName} - {bank.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Số tài khoản</label>
            <input 
              type="text" 
              required
              value={bankForm.accountNumber}
              onChange={(e) => setBankForm({...bankForm, accountNumber: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Nhập số tài khoản"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tên chủ tài khoản</label>
            <input 
              type="text" 
              required
              value={bankForm.accountHolder}
              onChange={(e) => setBankForm({...bankForm, accountHolder: e.target.value.toUpperCase()})}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="VD: NGUYEN VAN A"
            />
          </div>
          <button 
            type="submit" 
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Đang lưu..." : (bankAccount ? "Cập nhật tài khoản" : "Lưu tài khoản")}
          </button>
        </form>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-xl font-bold mb-4 text-gray-800">Yêu cầu rút tiền</h3>
        <p className="text-sm text-gray-600 mb-4">
          Hạn mức rút tối thiểu: <strong className="text-red-600">200.000đ</strong>. 
          Tiền sẽ được duyệt vào ngày 1 và 15 hàng tháng.
        </p>
        
        <form onSubmit={handleWithdraw} className="flex items-end gap-4 max-w-lg mb-8">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Số tiền muốn rút (đ)</label>
            <input 
              type="number" 
              required
              min="200000"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Nhập số tiền..."
            />
          </div>
          <button 
            type="submit" 
            disabled={withdrawing || !withdrawAmount}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {withdrawing ? "Đang gửi..." : "Rút Tiền"}
          </button>
        </form>

        <h4 className="text-lg font-semibold mb-3 text-gray-800">Lịch sử rút tiền</h4>
        {withdrawals.length === 0 ? (
          <p className="text-gray-500 italic">Chưa có lịch sử rút tiền nào.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mã / Ngày</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Số tiền</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ghi chú</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {withdrawals.map((w) => (
                  <tr key={w.id}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{w.id}</div>
                      <div className="text-xs text-gray-500">
                        {w.createdAt ? new Date(w.createdAt._seconds ? w.createdAt._seconds * 1000 : w.createdAt).toLocaleDateString("vi-VN") : ""}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-bold text-gray-900">
                      {w.amount.toLocaleString("vi-VN")}đ
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(w.status)}`}>
                        {getStatusText(w.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate" title={w.adminNote}>
                      {w.adminNote || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliatePayoutManager;
